import React, { useState, useRef, useEffect, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import MapView from './MapView';
import ControlPanel from './ControlPanel';
import Legend from './Legend';
import GoogleAQILayer from './GoogleAQILayer';
import { industriesAPI, warningsAPI } from '../../services/api';
import { fetchGoogleAQIData, fetchHourlyData } from './services/api';
import { simulateTick, triggerSpill, cleanSegment, pollutionLabel } from './simulation/engine';
import { computeWQI, wqiToColor, wqiGrade } from './simulation/wqi';

const DEFAULT_REGION = {
  token: process.env.REACT_APP_MAPBOX_TOKEN || '',
  lat:   21.13,
  lng:   81.5,
  zoom:  7,
  span:  0.3,
  baseWidth: 4,
  opacity: 0.9,
};

const DEFAULT_COLORS = {
  clean: '#22d3ee',
  mild:  '#a3e635',
  moderate: '#fb923c',
  severe: '#ef4444',
};

const AQ_REFRESH_MS = 5 * 60 * 1000;

function computeBbox(lat, lng, span) {
  return {
    minLat: lat - span / 2,
    minLon: lng - span / 2,
    maxLat: lat + span / 2,
    maxLon: lng + span / 2,
  };
}

export default function WaterSimulationPane({ height = '420px', defaultCenter, regionId }) {
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const simIntervalRef = useRef(null);

  // ── Tab ──
  const [activeTab, setActiveTab] = useState('water');

  // ── Water Simulation State ──
  const [regionConfig, setRegionConfig] = useState(() => ({
    ...DEFAULT_REGION,
    token: process.env.REACT_APP_MAPBOX_TOKEN || DEFAULT_REGION.token,
    ...(defaultCenter && { lat: defaultCenter.lat, lng: defaultCenter.lng }),
  }));
  const [colorConfig, setColorConfig] = useState(DEFAULT_COLORS);
  const [layers, setLayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [simRunning, setSimRunning] = useState(false);
  const [rainActive, setRainActive] = useState(false);
  const [tickSpeed, setTickSpeed] = useState(800);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [fetchReady, setFetchReady] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);
  const [toast, setToast] = useState('');

  // ── Platform Specific Industry State ──
  const [industries, setIndustries] = useState([]);

  // ── Google AQI State (Air Tab) ──
  const [gAQIData, setGAQIData] = useState(null);
  const [gAQILoading, setGAQILoading] = useState(false);
  const [gAQIError, setGAQIError] = useState(null);
  const [showGAQI, setShowGAQI] = useState(true);
  const [aqMapType, setAqMapType] = useState('INDIA_AQI');
  const [selectedCity, setSelectedCity] = useState(null);
  const [hourlyData, setHourlyData] = useState(null);
  const [hourlyLoading, setHourlyLoading] = useState(false);

  // ── Derived ──
  const bbox = computeBbox(regionConfig.lat, regionConfig.lng, regionConfig.span);
  const mapConfig = { ...regionConfig, bbox };

  const stats = layers.length > 0 ? {
    total:    layers.length,
    degraded: layers.filter(l => (l.wqi ?? 100) < 60).length,
    avgWQI:   Math.round(layers.reduce((s, l) => s + (l.wqi ?? 100), 0) / layers.length),
    minWQI:   Math.min(...layers.map(l => l.wqi ?? 100)),
  } : null;

  const liveSelected = selectedLayer ? layers.find(l => l.id === selectedLayer.id) ?? null : null;

  // ── Effects ──

  // Load industries for region
  useEffect(() => {
    if (!regionId) { setIndustries([]); return; }
    let cancelled = false;
    industriesAPI.getWithWaterData({ region_id: regionId })
      .then((res) => { if (!cancelled) setIndustries(res.data.data || []); })
      .catch(() => { if (!cancelled) setIndustries([]); });
    return () => { cancelled = true; };
  }, [regionId]);

  // Toast timeout
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  // Google AQI fetch
  const loadGAQI = useCallback(async (silent = false) => {
    if (!silent) setGAQILoading(true);
    setGAQIError(null);
    try {
      const data = await fetchGoogleAQIData();
      setGAQIData(data);
    } catch (e) {
      setGAQIError('Google AQI unavailable');
    } finally {
      setGAQILoading(false);
    }
  }, []);

  useEffect(() => {
    loadGAQI();
    const interval = setInterval(() => loadGAQI(true), AQ_REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadGAQI]);

  // Simulation tick
  useEffect(() => {
    clearInterval(simIntervalRef.current);
    if (simRunning && layers.length > 0) {
      simIntervalRef.current = setInterval(() => {
        setLayers(prev => {
          const next = prev.map(l => ({ ...l, params: { ...l.params } }));
          simulateTick(next, { rainActive });
          return next;
        });
      }, tickSpeed);
    }
    return () => clearInterval(simIntervalRef.current);
  }, [simRunning, tickSpeed, rainActive, layers.length]);

  // ── Handlers ──

  const handleLayerClick = useCallback(({ id, type, lngLat }) => {
    const layer = layers.find(l => l.id === id);
    if (!layer) return;
    setSelectedLayer(layer);
    if (popupRef.current) popupRef.current.remove();
    const { grade } = wqiGrade(layer.wqi ?? 100);
    const color = wqiToColor(layer.wqi ?? 100);
    popupRef.current = new mapboxgl.Popup({ closeButton: true, className: 'sim-popup' })
      .setLngLat(lngLat)
      .setHTML(`
        <div style="font-family:'Space Mono',monospace;font-size:11px;color:#e8eaf0;background:#111620;padding:10px 14px;border-radius:8px;border:1px solid rgba(255,255,255,0.1);min-width:180px">
          <div style="color:#6b7280;font-size:9px;letter-spacing:2px;text-transform:uppercase;margin-bottom:6px">${type}</div>
          <div style="font-size:12px;margin-bottom:8px">${layer.name || id}</div>
          <div style="display:flex;align-items:center;gap:10px;">
            <div style="width:36px;height:36px;border-radius:50%;border:2px solid ${color};display:flex;flex-direction:column;align-items:center;justify-content:center;">
              <span style="font-size:12px;font-weight:700;color:#e8eaf0">${layer.wqi ?? 100}</span>
            </div>
            <div>
              <div style="color:${color};font-weight:700;font-size:12px">${grade}</div>
              <div style="color:#6b7280;font-size:10px">WQI — ${pollutionLabel(layer.wqi ?? 100)}</div>
            </div>
          </div>
        </div>`)
      .addTo(mapRef.current);
  }, [layers]);

  const handleFetch = useCallback(() => {
    if (!regionConfig.token) { setToast('Enter Mapbox token'); return; }
    setFetchReady(true);
    setLoading(true);
    setFetchKey(k => k + 1);
  }, [regionConfig.token]);

  const handleSpill = useCallback(() => {
    if (!liveSelected) { setToast('Click a water body first'); return; }
    setLayers(prev => {
      const next = prev.map(l => ({ ...l, params: { ...l.params } }));
      triggerSpill(next, next.findIndex(l => l.id === liveSelected.id), 3);
      return next;
    });
    setToast('Industrial spill applied');
  }, [liveSelected]);

  const handleClean = useCallback(() => {
    if (!liveSelected) { setToast('Click a water body first'); return; }
    setLayers(prev => {
      const next = prev.map(l => ({ ...l, params: { ...l.params } }));
      cleanSegment(next, next.findIndex(l => l.id === liveSelected.id));
      return next;
    });
    setToast('Segment restored');
  }, [liveSelected]);

  const handleApplyWQI = useCallback((id, params) => {
    setLayers(prev => prev.map(l => {
      if (l.id !== id) return l;
      const { wqi } = computeWQI(params);
      return { ...l, params: { ...params }, wqi, color: wqiToColor(wqi) };
    }));
    setToast('WQI applied');
  }, []);

  const handleResetAll = useCallback(() => {
    setLayers(prev => prev.map(l => {
      const params = { ph: 7.2, cod: 20, bod: 3, do_: 8, turb: 8, nit: 4, temp: 22, col: 0 };
      const { wqi } = computeWQI(params);
      return { ...l, params, wqi, color: wqiToColor(wqi) };
    }));
    setToast('All reset to pristine');
  }, []);

  const handleWarnIndustry = useCallback(async (layer) => {
    if (!layer || !layer.industryId) {
      setToast('No linked industry for this water body.');
      return;
    }
    try {
      const wqi = layer.wqi ?? null;
      const severity = wqi != null && wqi < 40 ? 'critical' : wqi != null && wqi < 60 ? 'high' : 'medium';
      await warningsAPI.create({
        industry_id: layer.industryId,
        subject: 'Water contamination near monitored water body',
        message: `PrithviNet has detected degraded water quality (WQI ${wqi ?? 'N/A'}) in '${layer.name || layer.id}' likely influenced by your effluent discharge.`,
        action_items: ['Review treatment operations', 'Submit report in 24h'],
        priority: severity,
      });
      setToast('Warning sent.');
    } catch (e) { setToast('Failed to send warning.'); }
  }, []);

  const handleCityClick = useCallback(async (city) => {
    setSelectedCity(city);
    setHourlyLoading(true);
    try {
      const data = await fetchHourlyData(city.lat, city.lon);
      setHourlyData(data);
    } catch (e) { console.error(e); }
    finally { setHourlyLoading(false); }
  }, []);

  const handleLoaded = useCallback((n) => { setLoading(false); setToast(`Loaded ${n} water segments`); }, []);
  const handleFetchError = useCallback((msg) => {
    setLoading(false);
    setToast(msg || 'Overpass API error');
  }, []);

  const googleAqiKey = process.env.REACT_APP_GOOGLE_AQI_KEY || '';

  return (
    <div style={{ position: 'relative', width: '100%', height, minHeight: 400, background: '#0a0e14', borderRadius: 12, overflow: 'hidden' }}>
      {fetchReady && (
        <>
          <MapView
            config={mapConfig}
            layers={layers}
            setLayers={setLayers}
            onLayerClick={handleLayerClick}
            colorConfig={colorConfig}
            mapRef={mapRef}
            fetchKey={fetchKey}
            onLoaded={handleLoaded}
            onFetchError={handleFetchError}
            industries={industries}
            mode={activeTab === 'air' ? 'air' : 'water'}
          />
          <GoogleAQILayer
            mapRef={mapRef}
            visible={activeTab === 'air' && showGAQI}
            apiKey={googleAqiKey}
            mapType={aqMapType}
          />
          <Legend colorConfig={colorConfig} />
        </>
      )}

      {!fetchReady && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, background: '#0a0e14' }}>
          <div style={{ fontFamily: "'Space Mono',monospace", fontSize: 18, color: '#00d4ff' }}>PrithviNet simulation</div>
          <div style={{ fontSize: 12, color: '#6b7280', maxWidth: 280, textAlign: 'center' }}>Set Mapbox token and click <strong style={{ color: '#e8eaf0' }}>Load Water Bodies</strong> to start.</div>
        </div>
      )}

      <ControlPanel
        activeTab={activeTab} setActiveTab={setActiveTab}
        regionConfig={regionConfig} setRegionConfig={setRegionConfig}
        onFetch={handleFetch} loading={loading}
        simRunning={simRunning} setSimRunning={setSimRunning}
        rainActive={rainActive} setRainActive={setRainActive}
        tickSpeed={tickSpeed} setTickSpeed={setTickSpeed}
        colorConfig={colorConfig} setColorConfig={setColorConfig}
        layers={layers} selectedLayer={liveSelected}
        onSpill={handleSpill} onClean={handleClean}
        onResetAll={handleResetAll} onApplyWQI={handleApplyWQI}
        stats={stats} fetchReady={fetchReady}
        gAQIData={gAQIData} gAQILoading={gAQILoading} gAQIError={gAQIError}
        showGAQI={showGAQI} setShowGAQI={setShowGAQI} onRefreshGAQI={() => loadGAQI()}
        aqMapType={aqMapType} setAqMapType={setAqMapType}
        selectedCity={selectedCity} hourlyData={hourlyData} hourlyLoading={hourlyLoading}
        onCityClick={handleCityClick}
        onWarnIndustry={handleWarnIndustry}
      />

      {toast && (
        <div style={{ position: 'absolute', bottom: 24, right: 320, zIndex: 20, background: '#111620', border: '1px solid rgba(0,212,255,0.3)', borderRadius: 8, padding: '8px 14px', fontSize: 11, color: '#00d4ff', fontFamily: "'Space Mono',monospace", pointerEvents: 'none' }}>{toast}</div>
      )}

      <style>{`
        .mapboxgl-popup-content { background: transparent !important; padding: 0 !important; box-shadow: none !important; }
        .mapboxgl-popup-tip { display: none; }
      `}</style>
    </div>
  );
}
